import { ChangeEvent, FC, Fragment, useCallback, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Icons
import { FaTrash } from 'react-icons/fa';
// Types
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
// Models
import IAnswer from 'models/Answer';
import IBucket from 'models/Bucket';
// Components
import { IconButton } from 'components/Buttons';
import { Checkbox, Input } from 'components/Controls';
import { Tooltip } from 'components/Tooltip';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';
// 
import AnswerBucketScore from './AnswerBucketScore';
import MultiLanguageTextField from 'components/MultiLanguageTextField';

type Props = {
  index: number;
  questionIndex: number;
  onDelete?: (index:number) => void;

  questionRef: string;
}

const QuestionAnswer:FC<Props> = ({
  // Props
  index, questionIndex, onDelete, questionRef
}) => {
  const { t } = useTranslation();

  const name = `questions[${questionIndex}].answers`;

  const { control, watch, setValue, getValues, formState:{ errors } } = useFormContext();

  const { fields, replace } = useFieldArray({
    control,
    name: 'bucketScores'
  });

  const getBuckets = getValues('buckets');

  const getAnswerError = useCallback(() => {
    if ( !errors || !errors.questions ) return '';

    const question = (errors as any).questions[questionIndex];

    if ( !question || !question.answers ) return '';

    const answer = question.answers[index];

    return answer && answer.title ? answer.title.message : ''
    // eslint-disable-next-line
  }, [errors])();

  useEffect(() => {
    if ( getBuckets ){
      replace(getBuckets.map((bucket:IBucket) => ({
        ...bucket,
        score: 0
      })));
    }
    // eslint-disable-next-line
  }, [getBuckets]);

  useEffect(() => {
    if ( !getBuckets ) return;

    const currentBucketScores = getValues(`${name}.${index}.bucketScores`) || [];
    const isAligned = currentBucketScores.length === getBuckets.length
      && currentBucketScores.every((bucketScore:any, idx:number) => bucketScore.ref === getBuckets[idx].ref);

    if ( isAligned ) return;

    setValue(`${name}.${index}.bucketScores`, getBuckets.map((bucket:IBucket) => {
      const existing = currentBucketScores.find((bucketScore:any) => bucketScore.ref === bucket.ref);
      return { ref: bucket.ref, score: existing ? existing.score : 0 };
    }));
    // eslint-disable-next-line
  }, [getBuckets]);

  const handleClick = () => {
    if ( typeof onDelete === 'undefined' ) return;
    onDelete(index);
  }

  const handleChange = (onChange:any) => (event:ChangeEvent<HTMLInputElement>) => {
    const answers = watch(name);
    if ( watchedQuestionType === QuestionTypes.SingleChoice ){
      setValue(name, answers.map((answer:IAnswer, idx:number) => {
        const correct = index !== idx ? false : !answer.correct;
        return { ...answer, correct };
      }));
    }
    onChange(event.target.checked);
  }

  const watchedQuestionType = watch(`questions.${questionIndex}.type`);
  const watchedCorrect = watch(`${name}.${index}.correct`);
  const watchedAnswer = watch(`${name}.${index}`);
  const watchedReferenceAnswer = watch(`questions[${questionIndex}].config.referenceAnswer`);
  const watchedQuizType = watch('type');

  const isExpertAnswer = watchedReferenceAnswer && watchedReferenceAnswer.input.choice.ref === watchedAnswer.ref;

  const handleChangeExpertAnswer = () => {
    const nextReferenceAnswer = isExpertAnswer ? undefined : {
      score: 0,
      input: {
        choice: {
          ref: watchedAnswer.ref
        }
      }
    }
    setValue(`questions[${questionIndex}].config.referenceAnswer`, nextReferenceAnswer);
  }

  const showCorrectCheckbox = [QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedQuizType)
    && watchedQuestionType !== QuestionTypes.Ranking;

  if (!watchedAnswer) return null;
  return (
    <div className="flex-grow">
      <div className="flex gap-4 mb-1">
        <MultiLanguageTextField
          rules={{ required: isRequired, validate: noWhitespace }}
          className="mb-0 flex-grow"
          name={`${name}.${index}.title`}
          label={`${t('labels.answer')} ${index + 1}`}
          required={true}
          error={Boolean(getAnswerError)}
          errorText={getAnswerError}
          customKey={`questions[${questionRef}].answers.${watchedAnswer.ref}.title`}
        />
        {watchedQuestionType === QuestionTypes.Ranking ? (
          <Controller
            control={control}
            name={`${name}.${index}.postAnswerTitle`}
            render={({ field }) => (
              <Input
                {...field}
                id={`${name}.${index}.postAnswerTitle`}
                label={t('pages.quizForm.postAnswerTitle')}
              />
            )}
          />
        ) : null}
        {typeof onDelete !== 'undefined' ? (
          <div>
            <span className="block mb-1 h-5"></span>
            <Tooltip title={t('buttons.removeAnswer')}>
              <IconButton
                onClick={handleClick}
                ><FaTrash /></IconButton>
            </Tooltip>
          </div>
        ) : null}
      </div>
      {showCorrectCheckbox && (
        <Controller
          control={control} name={`${name}.${index}.correct`}
          render={({ field }) => (
            <Checkbox
              {...field}
              className="mb-1"
              label={t('pages.quizForm.correctAnswerLabel')}
              id={`${name}.${index}.correct`}
              checked={watchedCorrect}
              onChange={handleChange(field.onChange)}
            />
          )}
        />
      )}
      {watchedQuizType === QuizTypes.BeatTheExpert && (
        <Fragment>
          <Checkbox
            className="mb-0"
            label={t('pages.quizForm.expertAnswerLabel')}
            id={`questions[${questionIndex}].config.referenceAnswer.${watchedAnswer.ref}`}
            name={`questions[${questionIndex}].config.referenceAnswer.${watchedAnswer.ref}`}
            checked={isExpertAnswer || false}
            onChange={handleChangeExpertAnswer}
          />
          {isExpertAnswer ? (
            <div className="mt-2">
              <Controller
                control={control}
                name={`questions[${questionIndex}].config.referenceAnswer.score`}
                rules={{ required: isRequired }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`questions[${questionIndex}].config.referenceAnswer.score`}
                    type="number"
                    label={t('labels.score')}
                    required
                    error={Boolean(errors?.questions?.[questionIndex]?.config?.referenceAnswer?.score)}
                    errorText={errors?.questions?.[questionIndex]?.config?.referenceAnswer?.score?.message}
                  />
                )}
              />
            </div>
          ) : null}
        </Fragment>
      )}
      {watchedQuizType === QuizTypes.Personality && (
        <div className="border border-divider rounded p-4">
          <span className="block text-gray-400 text-sm font-light leading-5 mb-2">{t('pages.quizForm.bucketScoresLabel')}</span>
          <div className="
            grid
            grid-cols-1 landscape:grid-cols-2
            gap-4
          ">
            {fields.map((field:any, idx:number) => (
              <AnswerBucketScore
                key={`${index}-answer-bucket-item-${idx}`}
                questionIndex={questionIndex}
                answerIndex={index}
                index={idx}
                bucketRef={field.ref}
                bucketTitle={field.title}
              />
            ))}
          </div>
        </div>
      )}
      <span className="divider"></span>
    </div>
  )
}

export default QuestionAnswer;